import type { FormikErrors, FormikTouched } from 'formik';
import z, { ZodError } from 'zod';

import { promoEditorRemoveAllSchema, promoEditorSchema } from '@/schemas/PromoEditor.schema';
import type { PromoEditorInterface, PromoJob } from '@/types';

export const fieldErrorNoteFactory =
    (touched: FormikTouched<PromoEditorInterface>, errors: FormikErrors<PromoEditorInterface>) =>
    (fieldName: keyof PromoEditorInterface) =>
        touched[fieldName] && errors[fieldName] ? errors[fieldName] : undefined;

export const sortJobs = (jobs: PromoJob[]): PromoJob[] =>
    [...jobs].sort((a, b) => {
        const inProgressOrder = (b.status === 'processing' ? 1 : 0) - (a.status === 'processing' ? 1 : 0);
        if (inProgressOrder !== 0) {
            return inProgressOrder;
        }

        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

export const haveJobsChanged = (prevJobs: PromoJob[], newJobs: PromoJob[]): boolean => {
    if (prevJobs.length !== newJobs.length) {
        return true;
    }

    return prevJobs.some((prevJob, idx) => {
        const newJob = newJobs[idx];
        return (
            prevJob.jobId !== newJob?.jobId ||
            prevJob.status !== newJob.status ||
            prevJob.processedSkuCount !== newJob.processedSkuCount ||
            prevJob.successSkuCount !== newJob.successSkuCount ||
            prevJob.errorSkuCount !== newJob.errorSkuCount ||
            prevJob.updatedAt !== newJob.updatedAt
        );
    });
};

export const isJobWithinRecentDays = (createdAt: Date, days = 5): boolean => {
    const jobDate = new Date(createdAt);
    const now = new Date();
    const diffInDays = (now.getTime() - jobDate.getTime()) / (1000 * 60 * 60 * 24);
    return diffInDays <= days;
};

export const validatePromoForm = (values: PromoEditorInterface) => {
    try {
        if (values.action === 'remove-all') {
            promoEditorRemoveAllSchema.parse(values);
        } else {
            promoEditorSchema.parse(values);
        }

        return {};
    } catch (error) {
        if (error instanceof ZodError) {
            const fieldErrors = z.flattenError(error).fieldErrors as Record<string, string[] | undefined>;

            return Object.keys(fieldErrors).reduce(
                (acc, field) => {
                    acc[field] = fieldErrors[field]?.[0] ?? 'Invalid value';
                    return acc;
                },
                {} as Record<string, string>
            );
        }
    }
    return {};
};
